/** Angle-bracket tags (Grok TTS wrappers, etc.) — stripped for display; kept in raw message for voice. */
const ANGLE_BRACKET_TAG_RE = /<[^<>]+>/g;
const HEX_COLOR_RE = /^#[0-9a-fA-F]{3,8}$/;
const FLOAT_DELAY_RE = /^\d+(\.\d+)?s$/;

const DOUBLE_QUOTE_CHARS = new Set([
    '"',
    '\u201C',
    '\u201D',
    '\u201E',
    '\u201F',
    '\u00AB',
    '\u00BB',
    '\uFF02',
    '\u301D',
    '\u301E',
    '\u275D',
    '\u275E',
]);

function stripGrokVoiceWrapperTags(text) {
    return text.replace(ANGLE_BRACKET_TAG_RE, '');
}

function stripLeadingSpeakerArtifacts(text, characterName = null) {
    let cleaned = text;
    let previous = null;
    while (cleaned !== previous) {
        previous = cleaned;
        cleaned = cleaned.replace(/^(?:\[[^\]]+\]\s*:\s*)+/u, '');
        cleaned = cleaned.replace(/^(?:\s*:)+\s*/u, '');
        if (characterName) {
            const escaped = String(characterName).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            cleaned = cleaned.replace(new RegExp(`^(?:${escaped}\\s*:\\s*)+`, 'iu'), '');
        }
        cleaned = cleaned.replace(/^(?:Character\s*:\s*)+/iu, '');
    }
    return cleaned;
}

/**
 * When the model uses only quotes or only asterisks, wrap undelimited spans
 * in the missing delimiter. Mixed incomplete formatting is left alone.
 * @param {string} text
 * @returns {string}
 */
export function inferMissingDelimiters(text) {
    if (!text || typeof text !== 'string') {
        return text || '';
    }

    const segments = [];
    let plain = '';
    let i = 0;

    const flushPlain = () => {
        if (!plain) return;
        segments.push({ type: 'plain', raw: plain });
        plain = '';
    };

    while (i < text.length) {
        const ch = text[i];

        if (ch === '*' && text[i + 1] === '*') {
            flushPlain();
            let end = i + 2;
            while (end < text.length) {
                if (text[end] === '*' && text[end + 1] === '*') {
                    end += 2;
                    break;
                }
                end += 1;
            }
            segments.push({ type: 'bold', raw: text.slice(i, end) });
            i = end;
            continue;
        }

        if (ch === '*') {
            flushPlain();
            let end = i + 1;
            while (end < text.length && text[end] !== '*') {
                end += 1;
            }
            if (end < text.length) end += 1;
            segments.push({ type: 'asterisk', raw: text.slice(i, end) });
            i = end;
            continue;
        }

        if (DOUBLE_QUOTE_CHARS.has(ch)) {
            flushPlain();
            let end = i + 1;
            while (end < text.length && !DOUBLE_QUOTE_CHARS.has(text[end])) {
                end += 1;
            }
            if (end < text.length) end += 1;
            segments.push({ type: 'quote', raw: text.slice(i, end) });
            i = end;
            continue;
        }

        plain += ch;
        i += 1;
    }
    flushPlain();

    const hasContent = (raw) => /\S/.test(raw);
    const delimitedInner = (segment) => {
        const { type, raw } = segment;
        if (type === 'asterisk') {
            const closed = raw.length >= 2 && raw.endsWith('*');
            return closed ? raw.slice(1, -1) : raw.slice(1);
        }
        if (type === 'quote') {
            const closed = raw.length >= 2 && DOUBLE_QUOTE_CHARS.has(raw[raw.length - 1]);
            return closed ? raw.slice(1, -1) : raw.slice(1);
        }
        return raw;
    };

    const hasQuotes = segments.some((s) => s.type === 'quote' && hasContent(delimitedInner(s)));
    const hasAsterisks = segments.some((s) => s.type === 'asterisk' && hasContent(delimitedInner(s)));
    const hasPlain = segments.some((s) => s.type === 'plain' && hasContent(s.raw));

    if (hasQuotes && hasAsterisks && hasPlain) return text;
    if (!(hasPlain && ((hasQuotes && !hasAsterisks) || (hasAsterisks && !hasQuotes)))) {
        return text;
    }

    const wrapWith = hasQuotes && !hasAsterisks ? '*' : '"';

    return segments.map((segment) => {
        if (segment.type !== 'plain' || !hasContent(segment.raw)) {
            return segment.raw;
        }
        const leading = segment.raw.match(/^\s*/)?.[0] ?? '';
        const trailing = segment.raw.match(/\s*$/)?.[0] ?? '';
        const core = segment.raw.slice(leading.length, segment.raw.length - trailing.length);
        if (!core) return segment.raw;
        return `${leading}${wrapWith}${core}${wrapWith}${trailing}`;
    }).join('');
}

function formatText(text) {
    if (!text || typeof text !== 'string') {
        return text || '';
    }

    let formattedText = stripLeadingSpeakerArtifacts(stripGrokVoiceWrapperTags(text));
    formattedText = inferMissingDelimiters(formattedText);
    formattedText = formatHexColors(formattedText);
    formattedText = formatSizeLarge(formattedText);
    formattedText = formatSizeSmall(formattedText);
    formattedText = formatFloatingText(formattedText);
    formattedText = formatBold(formattedText);
    formattedText = formatItalic(formattedText);
    formattedText = formatHighlight(formattedText);
    formattedText = formatError(formattedText);
    formattedText = formatSuccess(formattedText);
    formattedText = formatHiddenText(formattedText);

    return formattedText;
}

function formatHiddenText(text) {
    return text.replace(/\[([^\]]+?)\]/g, '<span class="text-hidden" data-voice-text="$1"></span>');
}

function formatHexColors(text) {
    return text.replace(/#([0-9a-fA-F]{3,8})\s+([^#]+?)#/g, (match, color, content) => {
        const validHex = /^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$|^[0-9a-fA-F]{8}$/.test(color);
        if (validHex) {
            return `<span class="text-hex-color" style="--text-hex: #${color};">${content}</span>`;
        }
        return match;
    });
}

function formatFloatingText(text) {
    return text.replace(/@([^@]+?)@/g, (match, content) => {
        const letters = content.split('').map((letter, index) => {
            if (letter === ' ') return ' ';
            return `<span class="float-letter" style="--float-delay: ${index * 0.1}s;">${letter}</span>`;
        }).join('');

        return `<span class="text-floating">${letters}</span>`;
    });
}

function formatSizeLarge(text) {
    return text.replace(/\$\$([^$]+?)\$\$/g, '<span class="text-large">$1</span>');
}

function formatSizeSmall(text) {
    return text.replace(/(?<!\$)\$(?!\$)([^$]+?)\$(?!\$)/g, '<span class="text-small">$1</span>');
}

function formatBold(text) {
    return text.replace(/\*\*(?!\*)([^*]+?)\*\*(?!\*)/g, '<strong>$1</strong>');
}

function formatItalic(text) {
    let formattedText = '';
    let activeEmphasisTag = null;
    let isDialogueOpen = false;
    let isHtmlTagOpen = false;

    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        if (char === '<') {
            isHtmlTagOpen = true;
            formattedText += char;
            continue;
        }

        if (char === '>') {
            isHtmlTagOpen = false;
            formattedText += char;
            continue;
        }

        if (!isHtmlTagOpen && (char === '"' || char === '“' || char === '”')) {
            isDialogueOpen = char === '“' ? true : char === '”' ? false : !isDialogueOpen;
            formattedText += char;
            continue;
        }

        if (char !== '*') {
            formattedText += char;
            continue;
        }

        let runLength = 1;
        while (text[i + runLength] === '*') {
            runLength++;
        }

        if (runLength !== 1) {
            formattedText += '*'.repeat(runLength);
            i += runLength - 1;
            continue;
        }

        if (activeEmphasisTag) {
            formattedText += `</${activeEmphasisTag}>`;
            activeEmphasisTag = null;
            continue;
        }

        activeEmphasisTag = isDialogueOpen ? 'strong' : 'em';
        formattedText += `<${activeEmphasisTag}>`;
    }

    if (activeEmphasisTag) {
        formattedText += `</${activeEmphasisTag}>`;
    }

    return formattedText;
}

function formatHighlight(text) {
    return text.replace(/==([^=]+?)==/g, '<span class="text-highlight">$1</span>');
}

function formatError(text) {
    return text.replace(/!!([^!]+?)!!/g, '<span class="text-error">$1</span>');
}

function formatSuccess(text) {
    return text.replace(/\+\+([^+]+?)\+\+/g, '<span class="text-success">$1</span>');
}

export function setFormattedContent(element, content) {
    if (!element || !content) {
        if (element) element.textContent = content || '';
        return;
    }

    element.innerHTML = sanitizeHTML(formatText(content));
}

function sanitizeHTML(html) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const allowedTags = {
        'strong': [],
        'em': [],
        'span': ['class', 'style', 'data-voice-text']
    };

    function cleanNode(node) {
        if (node.nodeType === Node.TEXT_NODE) {
            return escapeHTML(node.textContent || '');
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
            const tagName = node.tagName.toLowerCase();

            if (!allowedTags.hasOwnProperty(tagName)) {
                return escapeHTML(node.textContent || '');
            }

            const allowedAttrs = allowedTags[tagName];
            let attrs = '';

            for (const attr of allowedAttrs) {
                if (!node.hasAttribute(attr)) continue;
                const value = node.getAttribute(attr);

                if (attr === 'style') {
                    const cleanStyle = sanitizeStyle(value);
                    if (cleanStyle) attrs += ` ${attr}="${cleanStyle}"`;
                } else if (attr === 'class') {
                    const cleanClass = sanitizeClass(value);
                    if (cleanClass) attrs += ` ${attr}="${cleanClass}"`;
                } else {
                    attrs += ` ${attr}="${escapeHTML(value)}"`;
                }
            }

            let childContent = '';
            for (const child of node.childNodes) {
                childContent += cleanNode(child);
            }

            return `<${tagName}${attrs}>${childContent}</${tagName}>`;
        }

        return '';
    }

    let result = '';
    for (const child of tempDiv.childNodes) {
        result += cleanNode(child);
    }

    return result;
}

function sanitizeStyle(style) {
    const cleanStyles = [];

    for (const styleRule of style.split(';').filter(s => s.trim())) {
        const [property, value] = styleRule.split(':').map(s => s.trim());
        if (!property || !value) continue;

        if (property === '--text-hex' && HEX_COLOR_RE.test(value)) {
            cleanStyles.push(`${property}: ${value}`);
        } else if (property === '--float-delay' && FLOAT_DELAY_RE.test(value)) {
            cleanStyles.push(`${property}: ${value}`);
        }
    }

    return cleanStyles.join('; ');
}

function sanitizeClass(className) {
    const allowedClasses = [
        'text-highlight', 'text-error', 'text-success', 'text-large',
        'text-small', 'text-floating', 'text-hidden', 'text-hex-color',
        'float-letter'
    ];
    return className.split(' ').filter(cls => allowedClasses.includes(cls.trim())).join(' ');
}

function escapeHTML(text) {
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
